import { axiosInstance } from "../axios";

// Updated signupContractor function
export const signupContractor = async (payload) => {
  // Now sends JSON, not FormData
  return await axiosInstance.post("profiles/signup-contractor/", payload, {
    withCredentials: true, // Keep if needed for session/CSRF
    headers: {
      "Content-Type": "application/json", // Change content type
    },
  });
};

export const resendVerification = async (email) => {
  return await axiosInstance.post("auth/resend-verification/", { email });
};

export const fetchContractorLogos = async (requestConfig) => {
  try {
    const response = await axiosInstance.get("profiles/contractors/logos/", requestConfig);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getChatPhotos = (axiosInstance, page = 1) => {
  return axiosInstance.get(`/chat/photos/?page=${page}`);
};

export const sendChatMessage = (axiosInstance, message, conversationId, imageUrl = null) => {
  const payload = { message: message, conversation_id: conversationId };
  if (imageUrl) payload.image_url = imageUrl;
  return axiosInstance.post("/chat/", payload, { timeout: 120000 });
};

export const sendQuoteRequest = async (axiosClient, requestData) => {
  return await axiosClient.post("quotes/send/", requestData);
};

export const sendGuestQuoteRequest = async (axiosPublic, quoteData) => {
    try {
        // Make sure the endpoint matches what you define in Django urls.py
        const response = await axiosPublic.post("/quotes/guest/send/", quoteData);
        return response.data;
    } catch (error) {
        console.error("Error sending guest quote request:", error.response?.data || error.message);
        throw error; // Re-throw to be caught by the component
    }
};

export const updateHomeownerProfile = async (axiosClient, formData) => {
  return await axiosClient.put("profiles/homeowner/update/", formData);
};

export const fetchRecentQuotes = async (axiosClient) => {
  try {
    const response = await axiosClient.get("quotes/recent/");
    return response.data;
  } catch (error) {
    throw error;
  }
};

// ============================================================================
// HOME HEALTH API
// ============================================================================

// Systems
export const fetchSystems = async (axiosClient) => {
  const response = await axiosClient.get("home/systems/");
  return response.data;
};

export const createSystem = async (axiosClient, data) => {
  const response = await axiosClient.post("home/systems/", data);
  return response.data;
};

export const updateSystem = async (axiosClient, id, data) => {
  const response = await axiosClient.put(`home/systems/${id}/`, data);
  return response.data;
};

export const deleteSystem = async (axiosClient, id) => {
  const response = await axiosClient.delete(`home/systems/${id}/`);
  return response.data;
};

export const fetchSystemDefaults = async (axiosClient) => {
  const response = await axiosClient.get("home/systems/defaults/");
  return response.data;
};

export const fetchSystemAIEstimate = async (axiosClient, data) => {
  const response = await axiosClient.post("home/systems/ai-estimate/", data);
  return response.data;
};

// Documents
export const fetchDocuments = async (axiosClient) => {
  const response = await axiosClient.get("home/documents/");
  return response.data;
};

// File upload validation constants
const MAX_DOCUMENT_SIZE_MB = 25;
const MAX_IMAGE_SIZE_MB = 10;
const MAX_DOCUMENT_SIZE_BYTES = MAX_DOCUMENT_SIZE_MB * 1024 * 1024;
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;
const ALLOWED_DOCUMENT_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);
const ALLOWED_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
]);

/**
 * Validate a file before upload.
 * @param {File} file - The file to validate
 * @param {"document"|"image"} type - Type of upload for size/type rules
 * @returns {{ valid: boolean, error?: string }}
 */
export const validateFileUpload = (file, type = "document") => {
  if (!file) return { valid: false, error: "No file selected." };

  const maxSize = type === "image" ? MAX_IMAGE_SIZE_BYTES : MAX_DOCUMENT_SIZE_BYTES;
  const maxSizeMB = type === "image" ? MAX_IMAGE_SIZE_MB : MAX_DOCUMENT_SIZE_MB;
  const allowedTypes = type === "image" ? ALLOWED_IMAGE_TYPES : ALLOWED_DOCUMENT_TYPES;

  if (file.size > maxSize) {
    return { valid: false, error: `File too large. Maximum size is ${maxSizeMB}MB.` };
  }

  if (file.type && !allowedTypes.has(file.type)) {
    const allowed = Array.from(allowedTypes).map(t => t.split("/")[1]).join(", ");
    return { valid: false, error: `File type not allowed. Accepted types: ${allowed}.` };
  }

  return { valid: true };
};

export const uploadDocument = async (axiosClient, formData) => {
  // Validate file if present in FormData
  const file = formData.get?.("file") || formData.get?.("document");
  if (file instanceof File) {
    const validation = validateFileUpload(file, "document");
    if (!validation.valid) {
      throw new Error(validation.error);
    }
  }
  const response = await axiosClient.post("home/documents/", formData);
  return response.data;
};

export const deleteDocument = async (axiosClient, id) => {
  const response = await axiosClient.delete(`home/documents/${id}/`);
  return response.data;
};

// Projects
export const fetchProjects = async (axiosClient) => {
  const response = await axiosClient.get("home/projects/");
  return response.data;
};

export const createProject = async (axiosClient, data) => {
  const response = await axiosClient.post("home/projects/", data);
  return response.data;
};

export const updateProject = async (axiosClient, id, data) => {
  const response = await axiosClient.put(`home/projects/${id}/`, data);
  return response.data;
};

export const deleteProject = async (axiosClient, id) => {
  const response = await axiosClient.delete(`home/projects/${id}/`);
  return response.data;
};

export const uploadProjectPhoto = async (axiosClient, projectId, formData) => {
  // Validate image file if present in FormData
  const file = formData.get?.("photo") || formData.get?.("image") || formData.get?.("file");
  if (file instanceof File) {
    const validation = validateFileUpload(file, "image");
    if (!validation.valid) {
      throw new Error(validation.error);
    }
  }
  const response = await axiosClient.post(
    `home/projects/${projectId}/photos/`,
    formData
  );
  return response.data;
};

export const deleteProjectPhoto = async (axiosClient, photoId) => {
  const response = await axiosClient.delete(`home/projects/photos/${photoId}/`);
  return response.data;
};

export const fetchProjectSuggestions = async (axiosClient) => {
  const response = await axiosClient.get("home/projects/suggestions/");
  return response.data;
};

export const fetchProjectAIPlan = async (axiosClient, data) => {
  const response = await axiosClient.post("home/projects/ai-plan/", data);
  return response.data;
};

// Dashboard & Alerts
export const fetchDashboardSummary = async (axiosClient) => {
  const response = await axiosClient.get("home/dashboard/");
  return response.data;
};

export const fetchAlerts = async (axiosClient) => {
  const response = await axiosClient.get("home/alerts/");
  return response.data;
};

export const dismissAlert = async (axiosClient, id) => {
  const response = await axiosClient.put(`home/alerts/${id}/dismiss/`);
  return response.data;
};

export const markAlertsRead = async (axiosClient) => {
  const response = await axiosClient.post("home/alerts/mark-read/");
  return response.data;
};

// Home Health - Seasonal Planner
export const fetchSeasonalPlan = async (axiosClient) => {
  const response = await axiosClient.get("home/seasonal-plan/");
  return response.data;
};

// Home Health - Neighborhood Activity
export const fetchNeighborhoodActivity = async (axiosClient) => {
  const response = await axiosClient.get("home/neighborhood-activity/");
  return response.data;
};

// Home Health - Valuation Impact
export const fetchValuationImpacts = async (axiosClient) => {
  const response = await axiosClient.get("home/valuation-impact/");
  return response.data;
};

// Messaging
export const fetchMessageThreads = async (axiosClient) => {
  const response = await axiosClient.get("messaging/threads/");
  return response.data;
};

export const fetchThreadDetail = async (axiosClient, threadId) => {
  const response = await axiosClient.get(`messaging/threads/${threadId}/`);
  return response.data;
};

export const sendMessage = async (axiosClient, threadId, data) => {
  const response = await axiosClient.post(`messaging/threads/${threadId}/messages/`, data);
  return response.data;
};

export const fetchUnreadCount = async (axiosClient) => {
  const response = await axiosClient.get("messaging/unread-count/");
  return response.data;
};

export const startMessageThread = async (axiosClient, data) => {
  const response = await axiosClient.post("messaging/threads/start/", data);
  return response.data;
};

// Emergency Quick-Connect
export const fetchEmergencyCategories = async (axiosClient) => {
  const response = await axiosClient.get("emergency/categories/");
  return response.data;
};

export const searchEmergencyContractors = async (axiosClient, data) => {
  const response = await axiosClient.post("emergency/search/", data);
  return response.data;
};

export const createEmergencyRequest = async (axiosClient, data) => {
  const response = await axiosClient.post("emergency/request/", data);
  return response.data;
};

// Contractor Response & Availability
export const fetchContractorAvailability = async (axiosClient) => {
  const response = await axiosClient.get("profiles/contractor/availability/");
  return response.data;
};

export const updateContractorAvailability = async (axiosClient, newStatus) => {
  const response = await axiosClient.put("profiles/contractor/availability/", {
    availability_status: newStatus,
  });
  return response.data;
};

export const refreshResponseMetrics = async (axiosClient) => {
  const response = await axiosClient.post("profiles/contractor/response-metrics/refresh/");
  return response.data;
};

// Push Notification Device Registration
export const registerFCMDevice = async (axiosClient, fcmToken, deviceType = "web") => {
  const response = await axiosClient.post("notifications/devices/register", {
    fcm_token: fcmToken,
    device_type: deviceType,
  });
  return response.data;
};

// Contractor Quote Inbox
export const fetchContractorQuoteInbox = async (axiosClient) => {
  const response = await axiosClient.get("quotes/contractor/inbox/");
  return response.data;
};

// Homeowner Referrals
export const fetchReferralCode = async (axiosClient) => {
  const response = await axiosClient.get("profiles/homeowner/referral-code/");
  return response.data;
};

export const fetchReferralStats = async (axiosClient) => {
  const response = await axiosClient.get("profiles/homeowner/referral-stats/");
  return response.data;
};

// Contractor Referrals (refer-a-friend — 404s while the feature flag is off)
export const fetchContractorReferralCode = async (axiosClient) => {
  const response = await axiosClient.get("profiles/contractor/referral-code/");
  return response.data;
};

export const fetchContractorReferralStats = async (axiosClient) => {
  const response = await axiosClient.get("profiles/contractor/referral-stats/");
  return response.data;
};

// Affiliate / Partner Programme
export const affiliateSignup = async (payload) => {
  return await axiosInstance.post("affiliate/signup/", payload, {
    headers: { "Content-Type": "application/json" },
  });
};

export const businessPartnerEnquiry = async (payload) => {
  return await axiosInstance.post("affiliate/business-enquiry/", payload, {
    headers: { "Content-Type": "application/json" },
  });
};
